import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { Select } from "components/common/Select";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import ListTable from "components/interpreterAccess/dashboard/ListTable";
import { InterpreterDashboardSelector } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAnalytics } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAppointments } from "slices/interpreter/dashboardSlice";
import { LegendButton } from "components/common/TableColor";
import { legends } from "components/common/TableColor";
import { useTranslation } from "react-i18next";

const DatesInputs = (({ control, errors }) => {
  const { t } = useTranslation();
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);

  return (
    <>
      <div className="row-primary">
        <h2>{t("dashboard.startDate")}</h2>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) =>
            <div className="input-image">
              <input ref={startDateRef} id='start_date' type="date" name='start_date' placeholder={t("dashboard.startDatePlaceholder")}
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(new Date(e.target.value)).format('YYYY/MM/DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />

            </div>
          } />
      </div>
      <div className="row-primary">
        <h2>{t("dashboard.endDate")}</h2>
        <Controller
          name="end_date"
          control={control}
          render={({ field }) =>
            <div className="input-image ">
              <input ref={endDateRef} type="date" name='end_date' placeholder={t("dashboard.endDatePlaceholder")}
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(new Date(e.target.value)).format('YYYY/MM/DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />
            </div>
          } />
      </div>
    </>
  )
});

function Dashboard() {
  const { t } = useTranslation();
  const dispatch = useDispatch()
  const { interpreterDashboard: dashboard, last_page: totalPages, loader, total } = useSelector(InterpreterDashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, setError: setErrors, reset, getValues } = useForm({
    defaultValues: {
      start_date: moment().startOf('month').format('YYYY-MM-DD'),
      end_date: moment().endOf('month').format('YYYY-MM-DD'),
    }
  });

  const [pageLength, setPageLength] = useState(10)
  const [filters, setFilters] = useState({
    start_date: moment().startOf("month").format("YYYY-MM-DD"),
    end_date: moment().endOf("month").format("YYYY-MM-DD"),
    language_id: "",
    account: "",
    service: "",
    status: "all"
  });

  const [activeLegend, setActiveLegend] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusValue, setStatusValue] = React.useState('all');
  useEffect(() => {
    dispatch(fetchInterpreterDashboardAnalytics())
    dispatch(fetchInterpreterDashboardAppointments(1, { pageLength: 10, start_date: moment().startOf('month').format('YYYY-MM-DD'), end_date: moment().endOf('month').format('YYYY-MM-DD'), sortBy: "id", sortDirection: "desc" }))
  }, [])


  // #region Language dropdown 
  const filteredLanguages = React.useMemo(() =>
    languages?.map(language => {
      const object = {
        value: language?.id,
        label: language?.name
      }
      return object
    }), [languages]);

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true))
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion


  // #region Legends 
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...legends];
    temp_array.unshift({
      value: "all",
      name: t("dashboard.legend.all"),
      id: 0,
      color: "#3176A0"
    })
    return temp_array;
  }, [legends, t]);

  const hanldeActiveLegend = (id, value) => {
    setActiveLegend(id);
    const newFilters = {
      ...filters,
      status: value,
      pageLength: pageLength,
    };
    setFilters(newFilters);
    dispatch(fetchInterpreterDashboardAppointments(1, newFilters));
    setStatusValue(value)
    setSearchTerm("");
  }

  //#endregion


  const onSubmit = (data) => {
    const newFilters = {
      ...filters,
      ...data,
      pageLength: pageLength,
    };
    setFilters(newFilters);
    setSearchTerm("");
    dispatch(fetchInterpreterDashboardAppointments(1, newFilters));
  };



  const handleResetFields = () => {
    setValue("start_date", "");
    setValue("end_date", "");
    setValue("language_id", "");
    setValue("account", "");
    setValue("department", "");
    setValue("facility", "");
    setValue("service", "");
    dispatch(fetchInterpreterDashboardAppointments(1, { sortBy: "id", sortDirection: "desc" }))
    clearErrors()
  };


  const serviceOption = [
    { value: 'In Person', label: t('dashboard.service.inPerson') },
    { value: 'VRI Pre Scheduled', label: 'VRI Pre Scheduled' },
    { value: 'VRI On Demand', label: 'VRI On Demand' },
    { value: 'OPI Pre Scheduled', label: t('dashboard.service.telephonic') },
    { value: 'OPI On Demand', label: t('dashboard.service.opiDemand') },
  ]

  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isforteen) => {
    const today = moment().format('YYYY-MM-DD');
    const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
    const next_fourteen_days = moment().add(15, 'days').format('YYYY-MM-DD');
    // tomorrow → tomorrow + 14 days

    if (isforteen) {
      // NEXT 14 DAYS (without today)
      setValue("start_date", tomorrow);
      setValue("end_date", next_fourteen_days);
    } else {
      // TODAY
      setValue("start_date", today);
      setValue("end_date", today);
    }

    setValue("service", item?.type || null);

    const data = {
      start_date: getValues("start_date"),
      end_date: getValues("end_date"),
      service: item?.type || "",
      pageLength: pageLength,
    };

    console.log("FINAL PAYLOAD SENT:", data);

    dispatch(fetchInterpreterDashboardAppointments(1, data));
    tableRef.current.scrollIntoView({ behavior: "smooth" });
    dispatch(setError({}));
    clearErrors();
  };



  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">{t("dashboard.title")}</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>{t("dashboard.today")}</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">{t("dashboard.today")}</th>
                    <th className="col-3 text-center">{t("dashboard.registered")}</th>
                    <th className="col-3 text-center">{t("dashboard.toCover")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setIsFourteenDays(false);
                        handleFilter(service, false);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td>{service?.registered}</td>
                      <td>{service?.cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>{t("dashboard.next14Days")}</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">{t("dashboard.today")}</th>
                    <th className="col-3 text-center">{t("dashboard.registered")}</th>
                    <th className="col-3 text-center">{t("dashboard.toCover")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setIsFourteenDays(true);
                        handleFilter(service, true);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td>{service?.registered}</td>
                      <td>{service?.cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* <ScheduleModal /> */}
        <div className="card_table">
          <div className="button-sm forLegends p-3">
            <LegendButton activeId={activeLegend} options={filteredStatuses} handleClick={hanldeActiveLegend} />
          </div>

          <form className="search_form_ad">
            <div className="row-primary dahboard_filter">
              <DatesInputs control={control} errors={errors} />
              <div className="row-primary">
                <h2>{t("dashboard.clients")}</h2>
                <Controller
                  name="account"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis" style={{ width: "100%" }}>
                      <input className="input-in type_inp" name="account" type="text" placeholder={t("dashboard.clientNamePlaceholder")} {...field} />
                    </div>
                  }
                />
              </div>
              {/* <div className="row-primary">
                <h2>Facility</h2>
                <Controller
                  name="facility"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis" style={{ width: "100%" }}>
                      <input className="input-in type_inp" name="facility" type="text" placeholder="Facility" {...field} />
                    </div>
                  }
                />
              </div> */}
              <div className="row-primary">
                <h2>{t("dashboard.language")}</h2>
                <Controller
                  name="language_id"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={field.value ? filteredLanguages?.find((opt) => field.value == opt.value) : ""}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>{t("dashboard.services")}</h2>

                <Controller
                  name="service"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <Select
                        name="service"
                        options={serviceOption || []}
                        {...field}
                        value={field.value ? serviceOption?.find((opt) => field.value == opt.value) : ""}
                      />
                    </div>
                  }
                />
              </div>
              {/* <div className="row-primary">
                <h2>Department</h2>
                <Controller
                  name="department"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis" style={{ width: "100%" }}>
                      <input className="input-in type_inp" name="department" type="text" placeholder="Department" {...field} />
                    </div>
                  }
                />
              </div> */}
            </div>

            <div className="row-primary btns_area row_10">
              <SubmitButton handleSubmit={handleSubmit(onSubmit)} buttonType="button" btnText={t("dashboard.search")} className={'green'} />
              <SubmitButton buttonType="button" className={'lightBlue'} btnText={t("dashboard.reset")} handleSubmit={handleResetFields} />
            </div>

            <div ref={tableRef}>
              <ListTable data={dashboard?.appointments} filters={filters} setPageLength={setPageLength} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </div>

          </form>
        </div>
      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;